import { ExpressCheckoutElement, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useState } from 'react';
import {
  NanoCheckoutFailureReason,
  trackNanoCheckoutFailed,
  trackNanoCheckoutPaymentSubmitted,
  trackNanoCheckoutPurchased,
} from '../../../lib/analytics/events';
import { ShippingAddress } from '../../../lib/checkout/address';
import { CardOnFile } from '../../../lib/checkout/useBillingPrefill';
import { CheckoutOffer } from '../../../lib/checkout/offer';
import { formatCents, PriceBreakdown } from '../../../lib/checkout/pricing';
import { customerMessageFromError, purchaseNanoUpsell } from '../../../lib/checkout/purchase';
import {
  CheckoutCustomerError,
  stripeCheckout,
  ThreeDSecureUnsupportedError,
} from '../../../lib/checkout/stripeCheckout';
import { Links } from '../../../lib/constants';
import styles from './CheckoutStep.module.scss';

export interface PaymentSectionProps {
  email: string;
  address: ShippingAddress;
  /** Stripe's own validation of the address currently in the element */
  addressComplete: boolean;
  /** the card already stored on the shopper's Recurly account, offered instead of the card form */
  cardOnFile: CardOnFile | null;
  offer: CheckoutOffer;
  breakdown: PriceBreakdown;
  petName: string;
  petId: string;
  gwa: boolean;
  onPurchased: () => void;
  /** hand off to the shop for the cases nano cannot finish, notably a 3DS challenge */
  onFallbackToShop: () => void;
}

/**
 * The card form and the purchase itself.
 *
 * Must render inside `StripeElementsProvider` -- `useStripe`/`useElements` need the Elements context.
 */
export default function PaymentSection({
  email,
  address,
  addressComplete,
  cardOnFile,
  offer,
  breakdown,
  petName,
  petId,
  gwa,
  onPurchased,
  onFallbackToShop,
}: PaymentSectionProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [useNewCard, setUseNewCard] = useState(false);

  const payingWithCardOnFile = !!cardOnFile && !useNewCard;

  const properties = { petId, gwa };

  const fail = (reason: NanoCheckoutFailureReason, message: string) => {
    trackNanoCheckoutFailed(properties, reason);
    setError(message);
  };

  /**
   * The whole purchase, from an on-screen payment method through to a completed order. Shared by the
   * card button and the wallet sheet, which is why `source` exists: the wallet sheet always produces
   * a fresh confirmation token, even while the card on file is the selected way to pay.
   */
  const submit = async (source: 'card' | 'wallet') => {
    if (!stripe || !elements || submitting) {
      return;
    }

    const chargeCardOnFile = source === 'card' && payingWithCardOnFile;

    // fail here rather than sending a bad address to recurly, which rejects the whole billing
    // address ("the billing address on your account is invalid") after the card has already been
    // stored -- so the shopper sees a payment error for what is actually a missing city or zip.
    // `addressComplete` is Stripe's own verdict on the element, which is the only check that catches
    // a malformed field rather than just an empty one.
    if (!addressComplete) {
      trackNanoCheckoutFailed(properties, 'address_error');
      setError('Please complete the shipping address above before confirming.');
      return;
    }

    setSubmitting(true);
    setError(null);
    trackNanoCheckoutPaymentSubmitted(properties);

    try {
      if (chargeCardOnFile) {
        // the card is already stored as billing info, so this call only saves the shipping address
        await stripeCheckout(null, email, address);
      } else {
        // must run before createConfirmationToken, or stripe throws
        const submitResult = await elements.submit();
        if (submitResult.error) {
          fail('card_declined', submitResult.error.message ?? 'Check your card details and try again.');
          return;
        }

        const { confirmationToken, error: tokenError } = await stripe.createConfirmationToken({ elements });
        if (tokenError || !confirmationToken) {
          fail('card_declined', tokenError?.message ?? 'We could not read that card. Try another.');
          return;
        }

        // stores the card as billing info and saves the shipping address in one call
        await stripeCheckout(confirmationToken.id, email, address);
      }

      // no token here on purpose: the card is already stored, which is the existing-user path
      await purchaseNanoUpsell(offer, breakdown.shippingCode);

      trackNanoCheckoutPurchased(properties);
      onPurchased();
    } catch (err) {
      if (err instanceof ThreeDSecureUnsupportedError) {
        trackNanoCheckoutFailed(properties, 'three_d_secure');
        onFallbackToShop();
        return;
      }
      if (err instanceof CheckoutCustomerError) {
        fail('card_declined', err.message);
        return;
      }
      // prefer whatever the server said it could tell the shopper: "that coupon has reached the
      // maximum number of redemptions on your account" is actionable, "please try again" is not.
      const serverMessage = customerMessageFromError(err);
      if (serverMessage) {
        fail('card_declined', serverMessage);
        return;
      }
      fail('service_error', 'Something went wrong taking that payment. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <span className={styles.sectionTitle}>Payment</span>
          {payingWithCardOnFile && (
            <button className={styles.editButton} type="button" onClick={() => setUseNewCard(true)}>
              Use a different card
            </button>
          )}
        </div>

        {/* apple pay / google pay. the wallet sheet does not collect a shipping address: registration
            already gave us one and the shopper reviewed it above, so asking again would let the two
            disagree. renders nothing when the browser has no wallet available. */}
        <div className={styles.expressCheckout}>
          <ExpressCheckoutElement
            options={{
              emailRequired: false,
              phoneNumberRequired: false,
              shippingAddressRequired: false,
              // Link is offered through the email field above, where it can autofill the card and
              // address. As an express button it instead takes the shopper out to Link's own sheet,
              // which is a second checkout competing with this one.
              paymentMethods: { link: 'never' },
            }}
            onConfirm={() => void submit('wallet')}
          />
        </div>

        {payingWithCardOnFile && cardOnFile ? (
          <div className={styles.addressLine}>
            {cardOnFile.brand} •••• {String(cardOnFile.lastFour).padStart(4, '0')} · Expires {cardOnFile.month}/
            {cardOnFile.year}
          </div>
        ) : (
          /* link must stay enabled here: stripe only runs the email lookup and OTP when link can
             actually pay, so suppressing it in this element disables link for the whole group and
             turns the email card above into a plain input */
          <PaymentElement />
        )}
      </div>

      {error && <div className={styles.fieldError}>{error}</div>}

      {/* One sentence next to the charge: what today buys, what happens after, and how to get out.
          The credit is deliberately not restated -- `OrderSummary` directly above already renders it
          as its own signed line, so the arithmetic is visible rather than claimed. Today's figure
          names the hardware as well as the months, because pricing only the membership let a reader
          assume the tracker was extra. The renewal figure comes from the offer, since it is a claim
          about what web will actually schedule.
          The renewal figure carries the only bold. Today's total is already the heaviest thing on the
          screen in OrderSummary's total row, so emphasising it again here says nothing; the renewal is
          the one fact in this sentence that appears nowhere else, and it is the part a skimmer must
          not miss. One bold, not two -- two in a 13px grey sentence reads striped and cancels out. */}
      <p className={styles.disclosure}>
        {formatCents(breakdown.totalInCents)} today covers the Fi Mini tracker and {petName}’s first 6 months
        {offer.renewalText ? (
          <>
            , then <strong>{offer.renewalText}</strong>
          </>
        ) : (
          ''
        )}{' '}
        — we’ll email you before it renews, and you can cancel any time from the app.
      </p>

      <div className={styles.ctaContainer}>
        <button className={styles.ctaPrimary} type="button" disabled={submitting} onClick={() => void submit('card')}>
          {submitting ? 'Confirming…' : 'Confirm'}
        </button>
        {/* the exit lives with the action rather than above it, so declining is always one tap away */}
        <a className={styles.ctaGhost} href={Links.manageHome}>
          Not now
        </a>
      </div>
    </>
  );
}
